"use client";

import React from "react";
import { CircleCheck as CheckCircle2, Wrench, ShieldCheck, Clock, DollarSign, ThermometerSnowflake, Droplets, Flame, Wind, OctagonAlert as AlertTriangle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function RecentPostsSidebar() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="flex flex-col gap-4">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-2 rounded-lg border p-4">
            <div className="h-4 w-3/4 animate-pulse rounded bg-muted"></div>
            <div className="h-3 w-full animate-pulse rounded bg-muted/50"></div>
            <div className="h-3 w-2/3 animate-pulse rounded bg-muted/50"></div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
        Unable to load recent articles at this time.
      </div>;
  }
  if (posts.length === 0) {
    return <p className="text-sm text-muted-foreground">No recent articles found.</p>;
  }
  return <div className="flex flex-col gap-4">
      {posts.map((post, i) => <Link key={post.id} to={post.link || "#"} data-index={i} className="group flex flex-col gap-2 rounded-lg border bg-card p-4 transition-colors hover:border-primary/50 hover:bg-accent/50">
          <h3 className="font-default text-sm font-semibold leading-tight text-card-foreground group-hover:text-primary">
            {post.title}
          </h3>
          {post.excerpt && <div className="line-clamp-2 text-xs text-muted-foreground" dangerouslySetInnerHTML={{
        __html: post.excerpt
      }} />}
          <span className="mt-1 text-xs font-medium text-primary">Read article →</span>
        </Link>)}
    </div>;
}
export default function ApplianceRepairArticle() {
  const wp_query_params = {
    post_type: "post",
    per_page: 4,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  const repairServices = [{
    name: "Refrigerators & Freezers",
    icon: ThermometerSnowflake,
    desc: "Cooling issues, leaks, frost buildup, and compressor issues.",
    href: "/RefrigeratorRepairPage"
  }, {
    name: "Washers & Dryers",
    icon: Droplets,
    desc: "Drainage problems, drum issues, spin cycles, and heating failures.",
    href: "/WasherRepairPage"
  }, {
    name: "Ovens, Stoves & Ranges",
    icon: Flame,
    desc: "Temperature inconsistencies, broken elements, burners, and igniter issues.",
    href: "/OvenAndStoveRepairPage"
  }, {
    name: "Dishwashers & Disposals",
    icon: Droplets,
    desc: "Drain clogs, leaks, wash cycle failures, and disposal motor jams.",
    href: "/DishwasherRepairPage"
  }];
  const processSteps = [{
    title: "Easy Scheduling",
    desc: "Request service online or call. We match you with available local pros for your appliance type."
  }, {
    title: "Thorough Diagnosis",
    desc: "The independent pro inspects the appliance to identify the mechanical or electrical root cause."
  }, {
    title: "Direct Quote",
    desc: "The pro gives their own quote for parts and labor before any repair work starts."
  }, {
    title: "Pro Repair",
    desc: "The technician completes the repair using appropriate replacement parts and tests the unit."
  }];
  const brands = ["Whirlpool", "Samsung", "LG", "GE Appliances", "Maytag", "KitchenAid", "Bosch", "Frigidaire", "Kenmore", "Sub-Zero"];
  const faqs = [{
    question: "My refrigerator is running but not cooling. What causes this?",
    answer: "Common causes include dirty condenser coils, a failing evaporator fan motor, or a defective start relay. When compressor issues arise, connect with a local pro who can inspect electrical circuits and refrigerant system components safely."
  }, {
    question: "Why is my washing machine leaking from the bottom?",
    answer: "Leaks often stem from a worn tub seal, cracked drain pump, loose internal hoses, or a faulty water inlet valve. Addressing leaks promptly helps protect flooring from moisture damage."
  }, {
    question: "Is it worth repairing an appliance that is over 10 years old?",
    answer: "It depends on the appliance type, overall condition, and repair cost compared to buying a replacement unit. Independent technicians can inspect your model and discuss whether a part repair makes economic sense."
  }, {
    question: "My dryer spins but does not produce any heat.",
    answer: "For electric dryers, this frequently indicates a failed heating element, blown thermal fuse, or cycling thermostat. For gas dryers, the culprit may be an igniter or gas solenoid coil. Restriced airflow in exhaust ducts can also cause thermal fuses to trip."
  }];
  return <section id="professionalappliancerepairarticle" className="bg-background py-16 md:py-24">
      <div className="container mx-auto max-w-7xl px-4 md:px-6">
        
        {}
        <header className="mb-12 max-w-3xl">
          <div className="mb-4 flex items-center gap-2 text-sm font-medium text-primary">
            <Wrench className="size-4" />
            <span>Homeowner Guide</span>
          </div>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Why Working with Local Appliance Pros Protects Your Home
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Learn about common appliance issues, compare repair options, and understand why connecting with independent local technicians provides safe, lasting results.
          </p>
          <div className="mt-6 flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/author_avatar.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/author_avatar.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/author_avatar.webp-wvc-sizes" alt="Handy Heroes home services editorial writer portrait" className="size-10 rounded-lg object-cover" loading="lazy" />
              <div>
                <p className="font-medium text-foreground">Handy Heroes Editorial Team</p>
                <p>Updated recently</p>
              </div>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          
          {}
          <article className="lg:col-span-8">
            
            {}
            <figure className="mb-10 overflow-hidden rounded-2xl border bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/appliance_repair_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/appliance_repair_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/appliance_repair_hero.png-wvc-sizes" alt="Technician inspecting wiring and drainage components on a modern residential washing machine" className="aspect-video w-full object-cover" loading="lazy" />
              <figcaption className="bg-card p-3 text-center text-xs text-muted-foreground">
                Local appliance technicians use diagnostic meters and manufacturer wiring diagrams to locate component failures.
              </figcaption>
            </figure>

            <div className="prose prose-gray dark:prose-invert max-w-none">
              <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
                When a major household appliance breaks down, everyday routines stall immediately. Washers stop draining mid-cycle, refrigerators lose temperature, or an oven fails right before meal preparation.
              </p>
              
              <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
                While clearing a drain filter or cleaning condenser coils is manageable for many homeowners, dealing with complex motors, control boards, heating elements, or gas lines requires trade experience. Connecting with independent local technicians through Handy Heroes brings skilled help to homes throughout <Link to="/ApplianceRepairAtlantaPage" className="text-primary hover:underline">Atlanta</Link>, <Link to="/MariettaPage" className="text-primary hover:underline">Marietta</Link>, <Link to="/GwinnettPage" className="text-primary hover:underline">Gwinnett</Link>, <Link to="/AlpharettaPage" className="text-primary hover:underline">Alpharetta</Link>, and <Link to="/RoswellPage" className="text-primary hover:underline">Roswell</Link>.
              </p>
              
              <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
                DIY troubleshooting on complex appliances carries risks of component damage, wiring faults, or water and gas leaks. Handy Heroes connects homeowners with independent service contractors who diagnose issues safely and outline repair options before starting work. If your home also requires system-level trades, we help you find pros for <Link to="/ElectricalServicesPage" className="text-primary hover:underline">electrical work</Link>, <Link to="/PlumbingServicesPage" className="text-primary hover:underline">plumbing services</Link>, or <Link to="/HVACServicesPage" className="text-primary hover:underline">heating and cooling</Link>.
              </p>

              <h2 className="font-default mt-12 mb-6 text-2xl font-bold text-foreground">The Hidden Costs of DIY Repair</h2>
              <p className="text-base leading-relaxed text-muted-foreground">
                It's easy to look at the hourly rate of a professional technician and think you're saving money by doing it yourself. However, this calculation often ignores several hidden costs:
              </p>
              <ul className="mt-4 flex flex-col gap-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <AlertTriangle className="mt-1 size-5 shrink-0 text-destructive" />
                  <span><strong>Misdiagnosis:</strong> Replacing the wrong part because you misidentified the problem means you're out the cost of the part, and the appliance is still broken.</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="mt-1 size-5 shrink-0 text-destructive" />
                  <span><strong>Tool Costs:</strong> Modern appliances often require specialized diagnostic tools and meters that the average homeowner doesn't possess.</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="mt-1 size-5 shrink-0 text-destructive" />
                  <span><strong>Time and Frustration:</strong> What takes a pro an hour might take you an entire weekend of frustration and multiple trips to the hardware store.</span>
                </li>
              </ul>

              <Separator className="my-12" />

              <h2 className="font-default mb-8 text-2xl font-bold text-foreground">Appliances Repaired by Local Pros</h2>
              <div className="mb-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {repairServices.map((service, i) => <Card key={i} data-index={i} className="border-border/50 bg-card/50 transition-colors hover:bg-card">
                    <CardContent className="flex items-start gap-4 p-6">
                      <div className="rounded-lg bg-primary/10 p-3 text-primary">
                        <service.icon className="size-6" />
                      </div>
                      <div>
                        <h3 className="font-default font-semibold text-foreground">
                          <Link to={service.href} className="text-foreground hover:text-primary transition-colors">
                            {service.name}
                          </Link>
                        </h3>
                        <p className="mt-1 text-sm text-muted-foreground">{service.desc}</p>
                      </div>
                    </CardContent>
                  </Card>)}
              </div>

              <div className="mb-10 rounded-xl border border-border bg-card p-6">
                <h3 className="font-default text-lg font-semibold text-foreground mb-3">Explore Specific Appliance Services</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                  <Link to="/RefrigeratorRepairPage" className="text-primary hover:underline">Refrigerator Repair</Link>
                  <Link to="/WasherRepairPage" className="text-primary hover:underline">Washer Repair</Link>
                  <Link to="/DryerRepairPage" className="text-primary hover:underline">Dryer Repair</Link>
                  <Link to="/DishwasherRepairPage" className="text-primary hover:underline">Dishwasher Repair</Link>
                  <Link to="/OvenAndStoveRepairPage" className="text-primary hover:underline">Oven & Stove Repair</Link>
                  <Link to="/RangeRepairPage" className="text-primary hover:underline">Range Repair</Link>
                  <Link to="/MicrowaveRepairPage" className="text-primary hover:underline">Microwave Repair</Link>
                  <Link to="/FreezerRepairPage" className="text-primary hover:underline">Freezer Repair</Link>
                  <Link to="/GarbageDisposalRepairPage" className="text-primary hover:underline">Garbage Disposal Repair</Link>
                </div>
              </div>

              <h2 className="font-default mb-6 text-2xl font-bold text-foreground">How the Marketplace Works</h2>
              <p className="mb-8 text-base leading-relaxed text-muted-foreground">
                Connecting with a qualified pro in the Atlanta metro is straightforward. Submit your project request, receive a prompt match, and review estimate details directly with your technician.
              </p>
              
              <div className="mb-12 space-y-8">
                {processSteps.map((step, i) => <div key={i} data-index={i} className="flex gap-4 md:gap-6">
                    <div className="flex flex-col items-center">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold">
                        {i + 1}
                      </div>
                      {i !== processSteps.length - 1 && <div className="mt-2 h-full w-px bg-border"></div>}
                    </div>
                    <div className="pb-8">
                      <h3 className="font-default text-xl font-semibold text-foreground">{step.title}</h3>
                      <p className="mt-2 text-muted-foreground">{step.desc}</p>
                    </div>
                  </div>)}
              </div>

              <Separator className="my-12" />

              <h2 className="font-default mb-6 text-2xl font-bold text-foreground">Clear Pricing and Contractor Standards</h2>
              <div className="mb-12 grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="flex flex-col items-center text-center">
                  <div className="mb-4 rounded-lg bg-secondary/10 p-4 text-secondary">
                    <DollarSign className="size-8" />
                  </div>
                  <h3 className="font-default font-semibold text-foreground">Direct Estimates</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Technicians provide a clear estimate covering parts and labor before work commences.</p>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="mb-4 rounded-lg bg-secondary/10 p-4 text-secondary">
                    <ShieldCheck className="size-8" />
                  </div>
                  <h3 className="font-default font-semibold text-foreground">Vetted Network</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Pros in our network are screened for professional standards and regional experience.</p>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="mb-4 rounded-lg bg-secondary/10 p-4 text-secondary">
                    <Clock className="size-8" />
                  </div>
                  <h3 className="font-default font-semibold text-foreground">Flexible Scheduling</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Choose appointment times that fit your schedule, with direct confirmation from your matched pro.</p>
                </div>
              </div>

              <h2 className="font-default mb-6 text-2xl font-bold text-foreground">Brands Repaired by Pros in the Network</h2>
              <p className="mb-6 text-base leading-relaxed text-muted-foreground">
                Connect with local pros who repair Whirlpool, Samsung, LG, GE Appliances, Maytag, KitchenAid, Bosch, Frigidaire, Kenmore, Sub-Zero, and other major household appliance brands:
              </p>
              <div className="mb-12 flex flex-wrap gap-2">
                {brands.map((brand, i) => <Badge key={i} data-index={i} variant="secondary" className="px-4 py-1.5 text-sm">
                    {brand}
                  </Badge>)}
                <Badge variant="outline" className="px-4 py-1.5 text-sm">
                  + Many More
                </Badge>
              </div>

              <Separator className="my-12" />

              <h2 className="font-default mb-6 text-2xl font-bold text-foreground">Common Appliance Problems Explained</h2>
              <p className="mb-8 text-base leading-relaxed text-muted-foreground">
                Curious about what might be wrong with your machine? Here are some of the most frequent issues pros encounter in the field.
              </p>
              
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`}>
                    <AccordionTrigger className="text-left text-base font-semibold">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>

              <div className="mt-12 rounded-2xl bg-primary/5 p-8 text-center border border-primary/10">
                <h3 className="font-default text-2xl font-bold text-foreground">Ready to get your appliance running again?</h3>
                <p className="mt-3 text-muted-foreground">Don't let a broken appliance disrupt your routine. Request to be connected with a local technician today.</p>
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link to="#bookingrequest">
                    <Button size="lg" className="w-full sm:w-auto">
                      Find a Pro Near You
                    </Button>
                  </Link>
                  <Link to="/FindAProPage">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto">
                      View All Trade Pros
                    </Button>
                  </Link>
                </div>
              </div>

            </div>
          </article>

          {}
          <aside className="lg:col-span-4">
            <div className="sticky top-24 flex flex-col gap-8">
              
              {}
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-6 text-center">
                  <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                    <CheckCircle2 className="size-6" />
                  </div>
                  <h3 className="font-default text-lg font-bold text-foreground">Need Fast Appliance Assistance?</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Our network of vetted local trade professionals is ready to help diagnose and resolve appliance malfunctions.
                  </p>
                  <Link to="#bookingrequest" className="mt-6 block">
                    <Button className="w-full">Request a Pro Now</Button>
                  </Link>
                </CardContent>
              </Card>

              {}
              <div className="rounded-xl border bg-card p-6 shadow-sm">
                <h3 className="font-default mb-4 flex items-center gap-2 text-lg font-bold text-foreground">
                  <span className="h-4 w-1 rounded-lg bg-primary"></span>
                  Latest Repair Tips
                </h3>
                <PostsProvider wp_query={{
                post_type: "post",
                per_page: 4,
                orderby: "date",
                order: "desc",
                status: ["publish"]
              }}>
                  <RecentPostsSidebar />
                </PostsProvider>
                <Link to="/HomePage" className="mt-4 inline-block text-sm font-medium text-primary hover:underline">
                  View all articles →
                </Link>
              </div>

              {}
              <div className="rounded-xl border bg-card p-6 shadow-sm">
                <h3 className="font-default mb-4 flex items-center gap-2 text-lg font-bold text-foreground">
                  <span className="h-4 w-1 rounded-lg bg-secondary"></span>
                  Popular Service Areas
                </h3>
                <ul className="flex flex-col gap-2 text-sm">
                  <li><Link to="/AtlantaPage" className="text-muted-foreground hover:text-primary hover:underline">Appliance Repair in Atlanta</Link></li>
                  <li><Link to="/MariettaPage" className="text-muted-foreground hover:text-primary hover:underline">Appliance Repair in Marietta</Link></li>
                  <li><Link to="/GwinnettPage" className="text-muted-foreground hover:text-primary hover:underline">Appliance Repair in Gwinnett</Link></li>
                  <li><Link to="/AlpharettaPage" className="text-muted-foreground hover:text-primary hover:underline">Appliance Repair in Alpharetta</Link></li>
                  <li><Link to="/RoswellPage" className="text-muted-foreground hover:text-primary hover:underline">Appliance Repair in Roswell</Link></li>
                </ul>
              </div>

            </div>
          </aside>

        </div>
      </div>
    </section>;
}